
import React from "react";

export default function ProductSearch({
  recipes,
  selectedRecipeId,
  setSelectedRecipeId,
  searchTerm,
  setSearchTerm,
  searchLoading,
  searchError,
  searchResults,
  setSearchResults,
  searchTescoProducts,
  addProductToRecipe,
  shoppingList,
  setShoppingList,
  justAdded,
  setJustAdded
}) {
  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-gray-100 min-h-screen p-6 rounded-3xl shadow-2xl max-w-3xl mx-auto glassmorphism">
      {/* Select recipe */}
      <div className="mb-6">
        <label className="block font-semibold mb-2 text-lg text-gray-700">Select Recipe to Add Products</label>
        <div className="flex gap-2 w-full" style={{ flexDirection: 'column' }}>
          <select
            value={selectedRecipeId || ""}
            onChange={(e) => setSelectedRecipeId(e.target.value)}
            className="border border-blue-200 bg-white/80 backdrop-blur-md p-3 rounded-xl shadow-sm w-full focus:ring-2 focus:ring-blue-400 transition-all text-gray-700"
          >
            <option value="">-- Select Recipe --</option>
            {recipes.map((r) => (
              <option key={r.id} value={r.id}>{r.title}</option>
            ))}
          </select>
          {selectedRecipeId && (
            <button
              className="ml-2 px-4 py-2 bg-gray-100 rounded-xl hover:bg-gray-300 text-sm font-semibold shadow-sm transition-all"
              onClick={() => setSelectedRecipeId("")}
              type="button"
              title="Clear selection"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Tesco product search */}
      <div className="mb-8">
        <label className="block font-semibold mb-2 text-lg text-gray-700">Search Tesco Products</label>
        <div className="flex gap-2 items-center">
          <div className="flex flex-col gap-2 w-full">
            <input
            type="text"
            placeholder="Search Tesco for products"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchTescoProducts()}
            className="border border-blue-200 bg-white/80 backdrop-blur-md p-3 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-400 transition-all text-gray-700 mb-0 w-full"
          />
          <button
            onClick={searchTescoProducts}
            disabled={searchLoading || !searchTerm.trim()}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-5 py-2 rounded-xl font-semibold shadow-md hover:from-blue-600 hover:to-blue-800 disabled:opacity-50 transition-all mb-0 w-full"
          >
            {searchLoading ? "Searching..." : "Search"}
          </button>
          <button
            onClick={() => {
              setSearchTerm("");
              setSearchResults([]);
            }}
            className="bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700 px-5 py-2 rounded-xl font-semibold shadow-md hover:from-gray-300 hover:to-gray-400 transition-all mb-0 w-full"
            disabled={searchLoading && searchTerm === "" && searchResults.length === 0}
            style={{ minWidth: '110px' }}
            type="button"
            title="Clear search and results"
          >
            Clear Results
          </button>
          </div>
        </div>

        {searchError && (
          <p className="text-red-600 mt-2 font-semibold">Error: {searchError}</p>
        )}

        {/* Sort controls */}
        {searchResults.length > 0 && (
          <div className="flex gap-2 my-4">
            <button
              onClick={() => setSearchResults([...searchResults].sort((a, b) => parseFloat(a.price.replace('£','')) - parseFloat(b.price.replace('£',''))))}
              className="bg-gradient-to-r from-gray-100 to-gray-300 px-4 py-2 rounded-xl text-sm font-semibold hover:from-gray-200 hover:to-gray-400 shadow-sm transition-all"
            >
              Price: Low to High
            </button>
            <button
              onClick={() => setSearchResults([...searchResults].sort((a, b) => parseFloat(b.price.replace('£','')) - parseFloat(a.price.replace('£',''))))}
              className="bg-gradient-to-r from-gray-100 to-gray-300 px-4 py-2 rounded-xl text-sm font-semibold hover:from-gray-200 hover:to-gray-400 shadow-sm transition-all"
            >
              Price: High to Low
            </button>
          </div>
        )}

        {/* Product tiles */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6 max-h-96 overflow-auto">
          {Array.isArray(searchResults) && searchResults.length > 0 ? (
            searchResults.map((product, i) => (
              <div key={i} className="bg-white/80 border border-blue-100 rounded-2xl p-4 flex flex-col items-center text-center shadow-lg backdrop-blur-md transition-all hover:shadow-xl hover:border-blue-300">
                {product.image && (
                  <img src={product.image} alt={product.title} className="w-24 h-24 object-contain mb-3 rounded-xl shadow-md" />
                )}
                <p className="text-base font-semibold leading-tight mb-1 text-gray-800 truncate" title={product.title}>{product.title}</p>
                <p className="text-green-700 font-bold text-lg mb-3">{product.price}</p>
                <div className="flex gap-2 w-full">
                  {selectedRecipeId && (
                    <button
                      className="bg-gradient-to-r from-green-500 to-green-700 text-white px-3 py-2 rounded-xl font-semibold shadow-md hover:from-green-600 hover:to-green-800 text-xs flex-1 transition-all"
                      onClick={() => addProductToRecipe(product.title, product.image, product.price, product.link, product.productId)}
                    >
                      Add to Recipe
                    </button>
                  )}
                  <button
                    className={`bg-gradient-to-r from-blue-500 to-blue-700 text-white px-3 py-2 rounded-xl font-semibold shadow-md hover:from-blue-600 hover:to-blue-800 text-xs flex-1 transition-all duration-300 ${justAdded === product.productId ? 'scale-110 bg-green-500' : ''}`}
                    onClick={() => {
                      setShoppingList((prev) => {
                        const idx = prev.findIndex((item) => item.productId === product.productId && item.link === product.link);
                        if (idx !== -1) {
                          // Already exists, increment quantity
                          return prev.map((item, i) =>
                            i === idx ? { ...item, quantity: item.quantity + 1 } : item
                          );
                        }
                        // New item
                        return [
                          ...prev,
                          {
                            title: product.title,
                            image: product.image,
                            price: product.price,
                            link: product.link,
                            productId: product.productId,
                            quantity: 1,
                          },
                        ];
                      });
                      setJustAdded(product.productId);
                      setTimeout(() => setJustAdded(null), 500);
                    }}
                  >
                    {justAdded === product.productId ? 'Added!' : 'Add to Shopping'}
                  </button>
                </div>
              </div>
            ))
          ) : (
            !searchLoading && <p className="col-span-2 text-gray-400 font-semibold">No results found</p>
          )}
        </div>
      </div>
    </div>
  );
}
